import { useForm, SubmitHandler } from "react-hook-form";
import { useState } from "react";
import CypherLeakLogo from "../../components/common/logo/CypherLeakLogo";
import InputForm from "../../components/common/input/InputText";
import { isDefined } from "../../utils/validation";
import PrimaryButton from "../../components/common/button/PrimaryButton";
import { useAuth } from "../../hooks/context/useAuth";
import { ToastContainer, toast } from "react-toastify";
import { toastOptions } from "../../data/toastOptions";
import { useNavigate } from "react-router";
import VerificationCodeInput from "../../components/common/input/VerificationCodeInput";
import classNames from "classnames";
import SecondaryButton from "../../components/common/button/SeconaryButton";
import { Link } from "react-router-dom";
import { defaultRoute } from "../../data/global";
import "react-toastify/dist/ReactToastify.css";

type Inputs = {
  username: string;
  password: string;
};

export default function Login() {
  const { register, handleSubmit, formState: { errors } } = useForm<Inputs>();

  // for redirect in case expired session
  const navigate = useNavigate();

  // if user has 2fa enabled
  const [isTwoFactor, setIsTwoFactor] = useState<boolean>(false);
  const [twoFactorCode, setTwoFactorCode] = useState<string | undefined>();

  // login API function
  const { login } = useAuth();

  // handle submit form data to login
  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const responseStatus: number = await login(data.username, data.password, twoFactorCode);

    switch (responseStatus) {
      // if success login
      case 200:
        navigate(defaultRoute, { replace: true });
        break;
      
      case 300:
        setIsTwoFactor(true);
        break;

      case 400:
        toast.error("Missing Username or Password", {
          ...toastOptions,
          toastId: "errorToast",
        });
        break;

      case 401:
        toast.error("Wrong Credentials", {
          ...toastOptions,
          toastId: "errorToast",
        });
        break;

      default:
        toast.error("Login Failed", {
          ...toastOptions,
          toastId: "errorToast",
        });
        break;
    }
  };

  return (
    <div className="login-pages">
      <div className="content-login-page">
        <div className="detail-login-page">
          {/* logo login cypherleak */}
          <div className="logo-login">
            <CypherLeakLogo />
          </div>
          {/* sign-in form */}
          <form className="form-login" onSubmit={handleSubmit(onSubmit)}>
            <div className={classNames({ "d-none": isTwoFactor })}>
              {/* username/email input */}
              <InputForm
                label="E-mail or phone number"
                info={errors.username?.message}
                isInputError={isDefined(errors.username?.message)}
                rest={{
                  type: "text",
                  ...register("username", { required: "Please fill out this required field" }),
                }}
              />
              {/* password input */}
              <InputForm
                label="Password"
                info={errors.password?.message}
                isInputError={isDefined(errors.password?.message)}
                rest={{
                  type: "password",
                  ...register("password", {
                    required: "Please fill out this required field",
                  }),
                }}
              />
            </div>
            {/* Verification Code Input | When Enabling 2FA */}
            {isTwoFactor && 
              <VerificationCodeInput
                twoFactorCode={twoFactorCode}
                onChangeCode={setTwoFactorCode}
                label="Enter the 6-digit code to finish login up."
              />
            }
            {/* Forgot Password */}
            <div className="d-flex justify-content-end form-check">
              <Link className="forgot-password" to={'/forgot-password'}>
                Forgot your password?
              </Link>
            </div>
            {/* sign-in button */}
            <div className="bloc-btn-default d-flex flex-column gap-3">
              <PrimaryButton rest={{ type: "submit" }}>
                Sign in <i className="icon-arrow-light-blanc" />
              </PrimaryButton>
              {/* Retourn to Login Form | When Enabling 2FA */}
              {isTwoFactor &&
                <SecondaryButton rest={{ onClick: () => setIsTwoFactor(false) }}>
                  Cancel
                </SecondaryButton>
              }
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}
